import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '@dukene/shared';
import { CheckCircle2, XCircle, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const verifyEmail = useAuthStore((state) => state.verifyEmail);

    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!token) {
            setStatus('error');
            setErrorMessage('Verification token is missing.');
            return;
        }

        const verify = async () => {
            try {
                await verifyEmail(token);
                setStatus('success');
            } catch (err: any) {
                setStatus('error');
                setErrorMessage(err.message || 'Verification failed. The link may be expired or invalid.');
            }
        };

        verify();
    }, [token, verifyEmail]);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <header className="p-8">
                <Link to="/" className="flex items-center gap-3 w-fit">
                    <div className="w-10 h-10 bg-black rounded-xl flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl font-black text-black tracking-tight">DUKENE</h1>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em] leading-none">Affiliates</p>
                    </div>
                </Link>
            </header>

            <main className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md text-center">
                    {status === 'loading' && (
                        <div className="space-y-6">
                            <div className="flex justify-center">
                                <Loader2 className="w-16 h-16 text-black animate-spin" />
                            </div>
                            <h2 className="text-2xl font-black text-black tracking-tight uppercase">Verifying Email</h2>
                            <p className="text-gray-400 font-light">Please wait while we confirm your account security details.</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="flex justify-center">
                                <div className="w-24 h-24 bg-zinc-50 rounded-3xl flex items-center justify-center">
                                    <CheckCircle2 className="w-12 h-12 text-black" />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h2 className="text-3xl font-black text-black tracking-tight uppercase">Email Verified</h2>
                                <p className="text-gray-400 font-light text-lg leading-relaxed">
                                    Your email has been confirmed. Your application is now in the review queue.
                                </p>
                            </div>

                            <div className="pt-4">
                                <Link
                                    to="/login"
                                    className="inline-flex items-center justify-center gap-3 px-8 h-14 bg-black text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-800 transition-all group"
                                >
                                    <span>Go to Dashboard</span>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>

                            <p className="text-[10px] text-gray-400 uppercase tracking-widest leading-relaxed max-w-[280px] mx-auto">
                                You will receive an email once our team approves your application.
                            </p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
                            <div className="flex justify-center">
                                <div className="w-24 h-24 bg-red-50 rounded-3xl flex items-center justify-center">
                                    <XCircle className="w-12 h-12 text-red-600" />
                                </div>
                            </div>

                            <div className="space-y-3">
                                <h2 className="text-3xl font-black text-black tracking-tight uppercase">Verification Failed</h2>
                                <p className="text-red-600 font-light">
                                    {errorMessage}
                                </p>
                            </div>

                            <div className="pt-4 space-y-4">
                                <Link
                                    to="/apply"
                                    className="inline-flex items-center justify-center gap-3 px-8 h-14 bg-black text-white rounded-2xl font-bold uppercase tracking-widest text-xs hover:bg-zinc-800 transition-all w-full"
                                >
                                    Return to Application
                                </Link>
                                <div>
                                    <Link to="/login" className="text-[10px] font-bold text-gray-400 uppercase tracking-widest hover:text-black transition-colors">
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="p-8 text-center">
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.2em]">
                    © {new Date().getFullYear()} Dukene Inc
                </p>
            </footer>
        </div>
    );
}
